import PropTypes from "prop-types";

import { Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { formatMoney } from "@/features/shared";
import { DownstreamTransactionLinks } from "@/features/ui";

export const LensProductSection = ({ purchaseOrderProductVariants }) => {
  const { hasPermission } = useAuth();

  if (!purchaseOrderProductVariants?.length) {
    return null;
  }

  const columns = [
    {
      field: "side",
      headerName: "Sisi",
      flex: 1,
      sortable: false,
    },
    {
      field: "sku",
      headerName: "SKU",
      flex: 3,
      sortable: false,
    },
    {
      field: "variant",
      headerName: "Varian",
      flex: 2,
      sortable: false,
      valueGetter: (_, row) => {
        // Get axis from note
        let axis = "-";
        try {
          const parsed = JSON.parse(row.note || "{}");
          axis = parsed.axis || "0";
        } catch {
          // Ignore JSON parse errors
        }

        // Parse prescription parts: {sph}/{cyl}/{add?}/{side?}
        const prescriptionParts = row.name
          ?.substring(row.name.lastIndexOf(" ") + 1)
          .split("/");
        const sph = prescriptionParts?.[0] || "-";
        const cyl = prescriptionParts?.[1] || "-";
        const add = prescriptionParts?.[2] || "0"; // Use "0" if null
        // Ignore prescriptionParts[3] (side indicator)

        // Build prescription
        const prescription = `${sph}/${cyl}/${axis}/${add}`;

        return prescription;
      },
    },
    {
      field: "variantNote",
      headerName: "PD/Prism",
      flex: 1.5,
      sortable: false,
      valueGetter: (_, row) => {
        let pd = "-",
          prism = "-";

        try {
          const parsed = JSON.parse(row.note || "{}");
          pd = parsed.pd || "-";
          prism = parsed.prism || "-";
        } catch {
          // Ignore JSON parse errors
        }

        // Build prescription format: {pd}/{prism}
        const prescription = `${pd}/${prism}`;

        return prescription;
      },
    },
    {
      field: "goodsReceipts",
      headerName: "Penerimaan",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.goodsReceipts}
          clickable={hasPermission(PermissionEnum.READ_GOODS_RECEIPTS)}
        />
      ),
    },
    {
      field: "purchaseInvoices",
      headerName: "Faktur",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.purchaseInvoices}
          clickable={hasPermission(PermissionEnum.READ_PURCHASE_INVOICES)}
        />
      ),
    },
    {
      field: "purchasePayments",
      headerName: "Pembayaran",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.purchasePayments}
          clickable={hasPermission(PermissionEnum.READ_PURCHASE_PAYMENTS)}
        />
      ),
    },
    {
      field: "costPerUnit",
      headerName: "Harga",
      flex: 1,
      type: "number",
      sortable: false,
      valueFormatter: (value) => formatMoney(value),
    },
  ];

  return (
    <Stack spacing={2}>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between"
          }}>
          <Typography variant="subtitle1">
            {purchaseOrderProductVariants[0]?.product?.name}
          </Typography>
          <Typography>
            Add On:{" "}
            {(() => {
              try {
                const note = purchaseOrderProductVariants[0]?.note || "{}";
                const parsed = JSON.parse(note);
                return parsed.addOn ? (
                  <Typography component="span" sx={{
                    fontWeight: "500"
                  }}>
                    {parsed.addOn}
                  </Typography>
                ) : (
                  "-"
                );
              } catch {
                return "-";
              }
            })()}
          </Typography>
        </Stack>
        <DataGrid
          getRowHeight={() => "auto"}
          columns={columns}
          rows={purchaseOrderProductVariants}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          hideFooter
        />
    </Stack>
  );
};

LensProductSection.propTypes = {
  purchaseOrderProductVariants: PropTypes.arrayOf(PropTypes.object).isRequired,
};
